import React, { useCallback, useState } from 'react';

import { ContentItem } from '@/components/sections/product-compare/compare-details/content-item';
import { Toggle } from '@/components/shared/data-display/toggle-compare';
import { ToggleGroup } from '@/components/shared/data-display/toggle-group';
import { Grid } from '@/components/shared/layout/grid';
import { getValueCompareItem } from '@/components/shared/utility/add-ons';
import { ICVCompareSection } from '@/contents/cv/cv-compare';
import { ILCVCompareSection } from '@/contents/lcv/lcv-compare';
import { CvModelClassWithCvModel } from '@/services/contentstack/models/cv-model-class';
import { LcvModel } from '@/services/contentstack/models/lcv-model';

interface CompareFeatureProps {
  /**
   * The items prop represents the features to be compared. It can be an array of
   * ILCVCompareSection or ICVCompareSection objects.
   */
  items: ILCVCompareSection[] | ICVCompareSection[];
  /**
   * The models prop represents the models against which the features are being
   * compared. It can be an array of LcvModel or CvModelClassWithCvModel objects.
   */
  models: LcvModel[] | CvModelClassWithCvModel[];
}

export const CompareFeature: React.FC<CompareFeatureProps> = ({
  items,
  models,
}: CompareFeatureProps) => {
  const [isOpens, setIsOpens] = useState<boolean[]>(
    Array.from(items as ILCVCompareSection[], () => false)
  );

  const onToggle = (index: number) => (open: boolean) => {
    const updateIsOpens = [...isOpens];
    updateIsOpens[index] = open;
    setIsOpens(updateIsOpens);
  };

  const getValue = useCallback(
    (
      model: LcvModel | CvModelClassWithCvModel,
      specType: string,
      labelId: string,
      labelFormat: string
    ) => {
      let value = getValueCompareItem(model, specType, labelId, labelFormat);

      if (value) {
        switch (specType) {
          case 'axle':
            value = value.split(', ');
            break;
          default: {
            break;
          }
        }
      }

      return value;
    },
    []
  );

  return (
    <ToggleGroup css={{ rowGap: 0, '.toggle-body': { px: 0 } }}>
      {items.map((sect, index) => (
        <Toggle
          css={{
            '@maxsm': {
              '> div': {
                px: '$space-0',
              },
            },
          }}
          key={index}
          title={sect.title}
          isOpen={isOpens[index]}
          size="sm"
          variant="white"
          isLine={true}
          onToggle={onToggle(index)}
          dataTest={`toggle_panel_${index}`}
        >
          <Grid
            templateColumns={{ '@initial': '2', '@lg': '3' }}
            columnGap={{ '@initial': '4', '@md': '6' }}
            rowGap="6"
            alignItems="baseline"
          >
            {sect.items?.map((label, labelIndex) => (
              <React.Fragment key={labelIndex}>
                {models?.map((model, modelIndex) => {
                  const value = getValue(
                    model,
                    sect.id,
                    label.id,
                    label.format
                  );
                  return (
                    <ContentItem
                      key={modelIndex}
                      title={label.title}
                      value={value || 'ไม่มี'}
                      test={`${sect.id}_${label.id}_${modelIndex}`}
                      css={
                        modelIndex === 2
                          ? { '@maxlg': { display: 'none' } }
                          : undefined
                      }
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </Grid>
        </Toggle>
      ))}
    </ToggleGroup>
  );
};

CompareFeature.displayName = 'CompareFeature';
